import { type UpsiderPermission } from '@src/api'
import {
  GetUsersMe200Response,
  GetUsersMe200ResponseAvailableFeaturesEnum,
} from '@src/api/generated/user-api'
import { L_STORAGE_KEYS } from '@src/consts'
import { lStorage } from '@src/utils'

export const getFreeeCompanyId = (): string | null => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user).freeeCompanyId : null
}

export const getIsBookkeeper = (): boolean => {
  const user = getUser()
  return user ? user.isBookkeeper : false
}

export const getIsGeneralUser = (): boolean => {
  const user = getUser()
  return user ? user.isGeneralUser : false
}

export const getIsJournalFeatureEnabled = (): boolean => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user).isJournalFeatureEnabled : false
}

export const getIsOrganizationAdmin = (): boolean => {
  const user = getUser()
  return user ? user.isOrganizationAdmin : false
}

export const getIsSystemAdmin = (): boolean => {
  const user = getUser()
  return user ? user.isSystemAdmin : false
}

export const getIsViewer = (): boolean => {
  const user = getUser()
  return user ? user.isViewer : false
}

export const getIsFrozen = (): boolean => {
  const user = getUser()
  return user ? user.isFrozen : false
}

export const getOrganizationId = (): string | null => {
  const user = getUser()
  return user ? user.organizationId : null
}

export const getOrganizationName = (): string | null => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user).organization.name : null
}

export const getOrgAccountId = (): string | null => {
  const user = getUser()
  return user ? user.orgAccountId : null
}

export const getUser = (): GetUsersMe200Response | null => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user) : null
}

export const getUserId = (): string | null => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user).id : null
}

export const getUserName = (): string | null => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  return user ? JSON.parse(user).userName : null
}

export const getAvailableFeatures =
  (): GetUsersMe200ResponseAvailableFeaturesEnum[] => {
    const user = lStorage.getItem(L_STORAGE_KEYS.USER)
    return user ? JSON.parse(user).availableFeatures : []
  }

export const getMailAddress = (): string | null => {
  const mailAddress = lStorage.getItem(L_STORAGE_KEYS.MAIL_ADDRESS)
  return mailAddress ?? null
}

export const getSmsPhoneNumber = (): string | null => {
  const smsPhoneNumber = lStorage.getItem(L_STORAGE_KEYS.SMS_PHONE_NUMBER)
  return smsPhoneNumber ?? null
}

export const getHasRole = (roleName: UpsiderPermission) => {
  try {
    const user = lStorage.getItem(L_STORAGE_KEYS.USER)
    return (
      user &&
      JSON.parse(user).roles?.[0]?.permissions?.some(
        (item: { permission: UpsiderPermission }) =>
          item.permission === roleName
      )
    )
  } catch (error) {
    // Handle case where user data is not yet loaded or roles are undefined
    return false
  }
}

// Check permission for Zeus
export const getHasPermissionByAllRole = (roleName: UpsiderPermission) => {
  const user = lStorage.getItem(L_STORAGE_KEYS.USER)
  if (!user) return false

  return JSON.parse(user).roles.some(
    (role: { permissions: { permission: UpsiderPermission }[] }) =>
      role.permissions.some(
        (item: { permission: UpsiderPermission }) =>
          item.permission === roleName || item.permission === 'SYSTEM_ADMIN'
      )
  )
}

/**
 * Check if the organization is an Adboost organization
 * An organization is considered Adboost if it has an active CARD_PAYMENT_EXTENSION contract
 * @param user - The authenticated user object
 * @returns true if the organization has an active CARD_PAYMENT_EXTENSION contract
 */
export const isAdboostOrganization = (
  user: GetUsersMe200Response | null | undefined
): boolean => {
  if (!user) {
    return false
  }

  const now = new Date()

  return user.contracts.some((contract) => {
    if (contract.contractType !== 'CARD_PAYMENT_EXTENSION') {
      return false
    }

    const startAt = new Date(contract.startAt)
    const endAt = new Date(contract.endAt)

    // Check if the contract is currently active (startAt <= now < endAt)
    return startAt <= now && now < endAt
  })
}
